import type { SubscriptionOrderSummary } from '@houzy/schemas/partner-management/partner-subscription-management';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { formatSwissAmount } from '../../../lib/formatSwissAmount';
import { PARTNER_SUBSCRIPTION_ADVANTAGE_KEYS } from '../../../lib/partnerSubscriptionAdvantages';
import { subscriptionCadenceLabel } from '../../../lib/subscriptionCadence';
import { formatSwissDate } from '../../../lib/time';
import { stateColor } from '../../../theme';
import { CheckoutSectionCard } from './CheckoutSectionCard';

export type CheckoutPlanCardProps = {
    plan: SubscriptionOrderSummary['plan'];
    agreedStartAt: SubscriptionOrderSummary['agreedStartAt'];
    // The onboarding sale lists the advantages on the plan-selection screen the craftsman just came from, so
    // repeating them on the order he is confirming only pushes the price down the page.
    showAdvantages?: boolean;
};

export function CheckoutPlanCard({ plan, agreedStartAt, showAdvantages = true }: CheckoutPlanCardProps): ReactElement {
    const { t } = useTranslation();

    return (
        <CheckoutSectionCard title={t('CHECKOUT.PLAN.TITLE')}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        px: 2,
                        py: 1.75,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'primary.main',
                        bgcolor: (theme) => stateColor(theme, 'hover'),
                    }}>
                    <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack spacing={0.5}>
                            {/* The heading colour a tinted panel uses, which MUI only exposes on the alert palette. */}
                            <Typography variant="h6" sx={{ color: 'Alert.infoColor' }}>
                                {plan.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('CHECKOUT.PLAN.PRICE_PER_MONTH', {
                                    amount: formatSwissAmount(plan.pricePerMonth),
                                })}{' '}
                                · {subscriptionCadenceLabel(t, plan.billingCadence)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'primary.main' }}>
                                {t('CHECKOUT.PLAN.COMMITMENT_TOTAL', {
                                    amount: formatSwissAmount(plan.commitmentTotal),
                                })}
                            </Typography>
                        </Stack>

                        {showAdvantages && (
                            // TODO: every plan advertises the same six advantages because `SubscriptionPlan` has no
                            // advantages column and nothing seeds one (see the TODO on the entity). Making them differ
                            // per plan needs that column, not six more translation keys.
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 1, sm: 2 }}
                                sx={{ alignItems: 'flex-start' }}>
                                {[
                                    PARTNER_SUBSCRIPTION_ADVANTAGE_KEYS.slice(0, 3),
                                    PARTNER_SUBSCRIPTION_ADVANTAGE_KEYS.slice(3),
                                ].map((column) => (
                                    <Stack key={column[0]} spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                        {column.map((key) => (
                                            <Stack
                                                key={key}
                                                direction="row"
                                                spacing={0.5}
                                                sx={{ alignItems: 'center' }}>
                                                <CheckIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {t(key)}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        <Typography variant="caption" color="text.secondary">
                            {t('CHECKOUT.PLAN.PRICES_EXCL_VAT')}
                        </Typography>
                    </Stack>

                    <Chip
                        size="small"
                        color="primary"
                        icon={
                            <Box
                                component="img"
                                src="/checkout-icons/selected-check.svg"
                                alt=""
                                aria-hidden
                                sx={{ width: 16, height: 16 }}
                            />
                        }
                        label={t('CHECKOUT.PLAN.SELECTED')}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                {agreedStartAt && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
                        <CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: 'action.active' }} />
                        <Typography variant="body2" color="text.secondary">
                            {t('CHECKOUT.PLAN.PLANNED_START', { date: formatSwissDate(new Date(agreedStartAt)) })}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </CheckoutSectionCard>
    );
}
